import { MsalProvider } from "@azure/msal-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { appWithTranslation } from "next-i18next";
import { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import "primeicons/primeicons.css";
import "../common/styles/globals.css";

import { NavigationGuardProvider } from "next-navigation-guard";
import { addLocale, PrimeReactProvider } from "primereact/api";
import { Suspense, useEffect } from "react";
import nextI18nextConfig from "../../next-i18next.config";
import { getAccessToken, msalInstance } from "../common/services/auth";
import AuthGate from "../common/services/auth/AuthGate";
import { MsalEventWrapper } from "../common/services/auth/MsalEventWrapper";
import { registerAccessTokenGetter } from "../common/services/auth/token";
import { SWRConfigWrapper } from "../common/services/swrConfigWrapper";
import { GlobalToast } from "../common/services/toast";
import "../common/services/tracking/routeHistory";
import { useRedirectStore } from "../common/store/useRedirectStore";
import { decodeToken } from "../common/utils";
import Frame from "./frame";

registerAccessTokenGetter(getAccessToken);

dayjs.extend(utc);
dayjs.extend(dayOfYear);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(quarterOfYear);
dayjs.extend(customParseFormat);

// Calendar translations
addLocale("de", {
    firstDayOfWeek: 1,
    dayNames: [
        "Sonntag",
        "Montag",
        "Dienstag",
        "Mittwoch",
        "Donnerstag",
        "Freitag",
        "Samstag",
    ],
    dayNamesShort: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    dayNamesMin: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    monthNames: [
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember",
    ],
    monthNamesShort: [
        "Jan",
        "Feb",
        "Mär",
        "Apr",
        "Mai",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Okt",
        "Nov",
        "Dez",
    ],
    today: "Heute",
    clear: "Löschen",
});

const PRIME_REACT_VALUES = {
    ripple: true,
};

function MyApp({ Component, pageProps }: AppProps) {
    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };
    const { setExternalRedirect, setLastCompanyId, setLastLocationId } =
        useRedirectStore();

    useEffect(() => {
        if (companyId) {
            setLastCompanyId(companyId);
            setLastLocationId(locationId ?? null);
        }
    }, [companyId, locationId, setLastCompanyId, setLastLocationId]);

    useEffect(() => {
        const hash = router.asPath.split("#")[1];

        const idToken = hash?.split("id_token=")?.[1];

        const payload = decodeToken(idToken)?.payload;

        if (payload?.returnTo) {
            const routeTo = encodeURIComponent(payload.returnTo);
            setExternalRedirect(routeTo);
        }
    }, [router.asPath, setExternalRedirect]);

    // Workaround for showing the correct cursor while dragging. See: https://github.com/react-dnd/react-dnd/issues/325
    useEffect(() => {
        document.body.style.cursor = "";
    }, []);

    return (
        <>
            <Head>
                <title>better.ui</title>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
                <link rel="icon" href="/favicon.ico" />
            </Head>
            <NavigationGuardProvider>
                {/* Gives every component access to MSAL (login state, tokens) */}
                <MsalProvider instance={msalInstance}>
                    {/* Reacts to MSAL events: password reset, cancelled login, back button */}
                    <MsalEventWrapper>
                        {/* AuthGate waits for the login check. While it waits, React shows this fallback: null = empty page */}
                        <Suspense fallback={null}>
                            {/* Shows the app only after the login check, or the error screen if login failed */}
                            <AuthGate>
                                <SWRConfigWrapper>
                                    <PrimeReactProvider
                                        value={PRIME_REACT_VALUES}
                                    >
                                        <Frame>
                                            <Component {...pageProps} />
                                        </Frame>
                                        {/* Render ToastComponent outside the Frame to prevent it from being hidden on Frame re-renders */}
                                        <GlobalToast />
                                    </PrimeReactProvider>
                                </SWRConfigWrapper>
                            </AuthGate>
                        </Suspense>
                    </MsalEventWrapper>
                </MsalProvider>
            </NavigationGuardProvider>
        </>
    );
}

export default appWithTranslation(MyApp, nextI18nextConfig);
